import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { MapPin, Globe, DollarSign, BarChart3, ArrowRight, Building2, Layers } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_COORDS = {
  'Ghana': { x: 28, y: 52 }, 'Nigeria': { x: 32, y: 52 }, 'Kenya': { x: 62, y: 58 },
  'Tanzania': { x: 60, y: 64 }, 'South Africa': { x: 50, y: 82 }, 'Zambia': { x: 52, y: 70 },
  'DRC': { x: 48, y: 60 }, 'Mozambique': { x: 60, y: 74 }, 'UAE': { x: 78, y: 38 },
  'Saudi Arabia': { x: 68, y: 35 }, 'Egypt': { x: 55, y: 30 }, 'Morocco': { x: 22, y: 28 },
  'Ethiopia': { x: 62, y: 48 }, 'Angola': { x: 40, y: 68 }, 'Senegal': { x: 18, y: 46 },
  'Tunisia': { x: 35, y: 24 }, 'Namibia': { x: 40, y: 78 }, 'Botswana': { x: 48, y: 78 },
  'Zimbabwe': { x: 54, y: 76 }, 'Uganda': { x: 58, y: 56 }, 'Rwanda': { x: 56, y: 60 },
  'Qatar': { x: 76, y: 36 }, 'Oman': { x: 80, y: 40 }, 'Kuwait': { x: 72, y: 32 },
  'Bahrain': { x: 75, y: 35 }, 'Jordan': { x: 62, y: 28 }, 'Iraq': { x: 68, y: 28 },
  'Algeria': { x: 30, y: 28 }, 'Libya': { x: 40, y: 28 }, 'Sudan': { x: 55, y: 42 },
  'Cameroon': { x: 38, y: 55 }, 'Madagascar': { x: 68, y: 74 }, 'Mali': { x: 25, y: 42 },
  'Niger': { x: 35, y: 40 }, 'Chad': { x: 42, y: 42 }, 'Gabon': { x: 36, y: 60 }
};

const getScaleSize = (capital, maxCapital) => {
  const ratio = capital / (maxCapital || 1);
  return Math.max(8, Math.min(28, 8 + ratio * 20));
};

export function RegionalMapPage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [mapData, setMapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState(null);

  useEffect(() => {
    fetchMapData();
  }, []);

  const fetchMapData = async () => {
    try {
      const res = await axios.get(`${API}/geo/deal-map`, { headers: getAuthHeaders() });
      setMapData(res.data);
    } catch (error) {
      console.error('Failed to fetch map data:', error);
    } finally {
      setLoading(false);
    }
  };

  const maxCapital = Math.max(...mapData.map(d => d.total_capital), 1);
  const totalDeals = mapData.reduce((sum, d) => sum + d.deal_count, 0);
  const totalCapital = mapData.reduce((sum, d) => sum + d.total_capital, 0);

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading map data...</div></div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="regional-map-page">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Globe size={20} />
          <h1 className="text-3xl font-bold tracking-tight">Regional Opportunity Map</h1>
        </div>
        <p className="text-muted-foreground">Infrastructure projects and capital requirements across Middle East & Africa</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Countries</p>
            <p className="text-2xl font-bold font-mono">{mapData.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Active Deals</p>
            <p className="text-2xl font-bold font-mono">{totalDeals}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Total Capital Required</p>
            <p className="text-2xl font-bold font-mono">{formatCurrency(totalCapital)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Avg Deal Size</p>
            <p className="text-2xl font-bold font-mono">{totalDeals > 0 ? formatCurrency(totalCapital / totalDeals) : '$0'}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><MapPin size={16} />MEA Deal Heatmap</CardTitle>
            <CardDescription>Click on a country marker to see deals</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative bg-muted rounded-sm overflow-hidden" style={{ aspectRatio: '16/10' }} data-testid="map-container">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
              <div className="absolute top-2 left-2 text-[10px] text-muted-foreground uppercase tracking-widest">Middle East & Africa</div>

              {mapData.map(country => {
                const coords = COUNTRY_COORDS[country.country];
                if (!coords) return null;
                const size = getScaleSize(country.total_capital, maxCapital);
                const isSelected = selectedCountry?.country === country.country;

                return (
                  <button
                    key={country.country}
                    className={`absolute transform -translate-x-1/2 -translate-y-1/2 rounded-full transition-all cursor-pointer hover:ring-2 hover:ring-foreground/30 ${
                      isSelected ? 'ring-2 ring-foreground bg-foreground/30' : 'bg-accent/60 hover:bg-accent/80'
                    }`}
                    style={{
                      left: `${coords.x}%`,
                      top: `${coords.y}%`,
                      width: `${size}px`,
                      height: `${size}px`
                    }}
                    onClick={() => setSelectedCountry(isSelected ? null : country)}
                    data-testid={`map-marker-${country.country.toLowerCase().replace(/\s+/g, '-')}`}
                    title={`${country.country}: ${country.deal_count} deals, ${formatCurrency(country.total_capital)}`}
                  />
                );
              })}

              {mapData.map(country => {
                const coords = COUNTRY_COORDS[country.country];
                if (!coords) return null;
                return (
                  <div
                    key={`label-${country.country}`}
                    className="absolute transform -translate-x-1/2 text-[8px] font-medium pointer-events-none whitespace-nowrap"
                    style={{ left: `${coords.x}%`, top: `${coords.y + 4}%` }}
                  >
                    {country.country}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {selectedCountry ? (
            <Card className="border-foreground/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin size={16} />{selectedCountry.country}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">Deals</p>
                    <p className="font-mono font-bold">{selectedCountry.deal_count}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Capital</p>
                    <p className="font-mono font-bold">{formatCurrency(selectedCountry.total_capital)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Sectors</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedCountry.sectors.map(s => (
                      <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Top Deals</p>
                  {selectedCountry.deals.map(deal => (
                    <div
                      key={deal.id}
                      className="flex items-center justify-between p-2 bg-muted rounded-sm cursor-pointer hover:bg-muted/80 transition-colors"
                      onClick={() => navigate(`/deals/${deal.id}`)}
                      data-testid={`map-deal-${deal.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{deal.name}</p>
                        <p className="text-[10px] text-muted-foreground">{deal.sector}</p>
                      </div>
                      <span className="text-xs font-mono ml-2">{formatCurrency(deal.capital_required)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground text-sm">
                <MapPin size={24} className="mx-auto mb-2 opacity-50" />
                Click a country marker to view deals
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 size={16} />Capital by Country
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {mapData.slice(0, 8).map(country => (
                <div key={country.country}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium">{country.country}</span>
                    <span className="font-mono text-muted-foreground">{formatCurrency(country.total_capital)}</span>
                  </div>
                  <Progress value={(country.total_capital / maxCapital) * 100} className="h-1.5" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
